import {
  bonusDetailsCta,
  bonusDetailsTypeLabel,
  bonusDisplayStatus,
  bonusDisplayStatusTone,
  bonusExpiryCountdown,
  bonusProgressAmountsLabel,
  bonusProgressPercent,
  bonusTermsMetaRows,
  formatBonusAmount,
  formatBonusDate,
  isRewardBonusType,
  type BonusTransactionDetails,
} from '@/domain/bonuses';
import { cn } from '@/lib/cn';
import { Badge } from '../common/badge';
import { Button } from '../common/button';
import { BonusProgress } from './BonusProgress';

export interface BonusDetailsPanelProps {
  details: BonusTransactionDetails;
  currency?: string;
  termsLabel?: string;
  termsFallback?: string;
  bonusAmountLabel?: string;
  rewardAmountLabel?: string;
  wageringProgressLabel?: string;
  startDateLabel?: string;
  endDateLabel?: string;
  onCta?: (kind: 'continueBetting' | 'useFreeBet' | 'viewTicket', ticketId?: string) => void;
  className?: string;
}

export function BonusDetailsPanel({
  details,
  currency = '',
  termsLabel = 'Terms',
  termsFallback = '',
  bonusAmountLabel = 'Bonus amount',
  rewardAmountLabel = 'Reward amount',
  wageringProgressLabel = 'Wagering progress',
  startDateLabel = 'Start date',
  endDateLabel = 'End date',
  onCta,
  className,
}: BonusDetailsPanelProps) {
  const isReward = isRewardBonusType(details.Type);
  const statusLabel = bonusDisplayStatus(details);
  const cta = bonusDetailsCta(details);
  const metaRows = bonusTermsMetaRows(details, currency);
  const terms = (details.Note ?? '').trim() || termsFallback;
  const expiry = bonusExpiryCountdown(details.ExpiryDate);
  const startLabel = formatBonusDate(details.StartDate);
  const endLabel = formatBonusDate(details.ExpiryDate);

  return (
    <div
      className={cn(
        'flex w-full max-w-[560px] flex-col gap-6 rounded-md border border-border-default',
        'bg-bg-surface p-6',
        className,
      )}
    >
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[22px] font-bold text-text-primary">
            {details.Name || 'Bonus'}
          </h2>
          <Badge tone={bonusDisplayStatusTone(statusLabel)} size="sm">
            {statusLabel}
          </Badge>
        </div>
        <p className="text-[13px] text-text-secondary">
          {bonusDetailsTypeLabel(details.ModuleId)}
        </p>
      </header>

      <div className="flex flex-col gap-1">
        <p className="text-[12px] text-text-secondary">
          {isReward ? rewardAmountLabel : bonusAmountLabel}
        </p>
        <p className="text-[28px] font-bold text-text-primary">
          {formatBonusAmount(details.Amount, currency)}
        </p>
      </div>

      {!isReward ? (
        <div className="flex flex-col gap-3 rounded-md bg-bg-elevated p-4">
          <p className="text-[13px] font-bold text-text-primary">{wageringProgressLabel}</p>
          <BonusProgress
            amounts={bonusProgressAmountsLabel(details, currency)}
            percent={bonusProgressPercent(details)}
            size="large"
          />
        </div>
      ) : null}

      {startLabel || endLabel ? (
        <div className="flex gap-6 rounded-md bg-[#2a2620] px-4 py-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-[12px] text-[#b8afa3]">{startDateLabel}</p>
            <p className="text-[14px] font-bold text-[#faf8f5]">{startLabel || '—'}</p>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-[12px] text-[#b8afa3]">{endDateLabel}</p>
            <p className="text-[14px] font-bold text-[#faf8f5]">{endLabel || '—'}</p>
          </div>
        </div>
      ) : null}

      {expiry ? <p className="text-[13px] text-text-secondary">{expiry}</p> : null}

      {terms ? (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-bold text-text-primary">{termsLabel}</p>
          <p className="text-[12px] text-text-secondary whitespace-pre-wrap">{terms}</p>
        </div>
      ) : null}

      {metaRows.length > 0 ? (
        <dl className="flex w-full flex-col text-[12px]">
          {metaRows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className={cn(
                'flex items-center justify-between gap-4 py-2',
                index < metaRows.length - 1 ? 'border-b border-[#7a7164]' : null,
              )}
            >
              <dt className="text-[#b8afa3]">{row.label}</dt>
              <dd className="shrink-0 text-right font-bold text-[#faf8f5]">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {cta ? (
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={() =>
            onCta?.(cta.kind, cta.kind === 'viewTicket' ? cta.ticketId : undefined)
          }
        >
          {cta.label}
        </Button>
      ) : null}
    </div>
  );
}
